import './YouTubePlanning.scss';
import { useEffect, useMemo, useState } from 'react';
import { Page } from '@Components/Page';
import { FullPageSpinner } from '@Components/Spinner';
import { useSetError } from '../../redux/modals';
import { useExperimentFlag } from '../../utils/experiment-context';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useTabbedNav } from '../../utils/hooks/useNav';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';
import { BuildRecommendationTab } from './BuildRecommendationTab';
import { CreatorSetsTab } from './CreatorSets/CreatorSetsTab';
import { DiscoverTab } from './Discover/DiscoverTab';
import { useGoogleAdsAccount } from './hooks/use-google-ads-account';
import {
  CREATOR_DISCOVERY_EXPERIMENT,
  CREATOR_TABS,
  PAGE_TITLE,
  Tab,
  TabValue,
  TAB_NAVS
} from './youtube-planning-constants';
import { BuildRecommendationPrefill, GoogleAdsAccount } from './youtube-planning-types';

export const YouTubePlanning = () => {
  const { cid } = useCompanyInfo();
  const setError = useSetError();
  const creatorDiscoveryEnabled = useExperimentFlag(CREATOR_DISCOVERY_EXPERIMENT);

  const navs = useMemo(
    () =>
      creatorDiscoveryEnabled ? TAB_NAVS : TAB_NAVS.filter(nav => !CREATOR_TABS.includes(nav.key)),
    [creatorDiscoveryEnabled]
  );

  const { tab, goToTab } = useTabbedNav<TabValue>({
    baseURL: 'youtube/planning',
    defaultKey: Tab.BUILD,
    navs: navs.map(nav => nav.key)
  });
  // useTabbedNav accepts any tab segment when there's only one nav, so a creator tab URL opened
  // without the experiment falls back to Build Recommendation here.
  const activeTab = navs.some(nav => nav.key === tab) ? tab : Tab.BUILD;

  // Values a Creator Set hands to Build Recommendation. BuildRecommendationTab reads them only
  // when it mounts, so they are cleared once that tab shows. Any later visit (nav click,
  // browser Back/Forward) then starts empty.
  const [buildPrefill, setBuildPrefill] = useState<BuildRecommendationPrefill>();
  const openBuildRecommendation = (prefill: BuildRecommendationPrefill) => {
    setBuildPrefill(prefill);
    goToTab(Tab.BUILD);
  };
  useEffect(() => {
    if (activeTab === Tab.BUILD) {
      setBuildPrefill(undefined);
    }
  }, [activeTab]);

  // Forwarded to <Page isLoading> so MxE.PageDataLoad is timed.
  const {
    data: adsAccount,
    isLoading: accountLoading,
    error: accountError
  } = useGoogleAdsAccount(cid);

  useEffect(() => {
    if (accountError) {
      setError({ message: accountError.message, reportError: accountError });
    }
  }, [accountError, setError]);

  return (
    <Page
      title={PAGE_TITLE}
      pageType={PAGE_TITLE}
      navs={navs}
      selectedNav={activeTab}
      onNav={key => {
        goToTab(key as TabValue);
        Mixpanel.track(MxE.TabChange, { page: PAGE_TITLE, option_selected: key });
      }}
      isLoading={accountLoading}
    >
      {cid ? (
        <div className='youtube-planning-body'>
          <TabBody
            tab={activeTab}
            adsAccount={adsAccount}
            noAdsAccount={!adsAccount && Boolean(accountError)}
            buildPrefill={buildPrefill}
            onUseInBuild={openBuildRecommendation}
          />
        </div>
      ) : (
        <FullPageSpinner />
      )}
    </Page>
  );
};

interface TabBodyProps {
  tab: TabValue;
  adsAccount?: GoogleAdsAccount;
  noAdsAccount: boolean;
  buildPrefill?: BuildRecommendationPrefill;
  onUseInBuild: (prefill: BuildRecommendationPrefill) => void;
}

const TabBody = ({ tab, adsAccount, noAdsAccount, buildPrefill, onUseInBuild }: TabBodyProps) => {
  if (tab === Tab.DISCOVER)
    return <DiscoverTab adsAccount={adsAccount} noAdsAccount={noAdsAccount} />;
  if (tab === Tab.SETS)
    return <CreatorSetsTab adsAccount={adsAccount} onUseInBuild={onUseInBuild} />;
  return <BuildRecommendationTab adsAccount={adsAccount} prefill={buildPrefill} />;
};
